import { motion } from 'motion/react';
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { useT } from '../app/i18n.tsx';
import { useTheme } from '../app/theme.tsx';
import { niceStep } from './chart-scale.ts';

// The net-worth "horizon": one crimson line over a wash of the same colour, with a few dashed
// grid lines and their amounts on the left (as Playloft's rating chart; the owner, 30.09.2026:
// "the same shade of red and those horizontal lines with numbers"). The line is always drawn
// through the same number of samples, so a new range morphs the curve instead of redrawing it.
// Hover shows a hairline, a point and a floating label with the exact value.

type HorizonPoint = { date: string; valueMinor: bigint };

const SAMPLES = 72;
const PAD_TOP = 14;
const PAD_BOTTOM = 6;
/** Room on the left for the grid amounts. */
const AXIS = 48;

/** Evenly re-sampled values (linear between neighbours), so every range has the same shape count. */
function resample(points: readonly HorizonPoint[]): { date: string; value: number }[] {
  if (points.length === 0) return [];
  if (points.length === 1) return Array.from({ length: SAMPLES }, () => ({ date: points[0]!.date, value: Number(points[0]!.valueMinor) }));
  return Array.from({ length: SAMPLES }, (_, i) => {
    const at = (i / (SAMPLES - 1)) * (points.length - 1);
    const low = Math.floor(at);
    const high = Math.min(points.length - 1, low + 1);
    const share = at - low;
    const a = Number(points[low]!.valueMinor);
    const b = Number(points[high]!.valueMinor);
    return { date: (share < 0.5 ? points[low] : points[high])!.date, value: a + (b - a) * share };
  });
}

/** A smooth path through the points (monotone cubic, so it never overshoots between samples). */
function smoothPath(xs: readonly number[], ys: readonly number[]): string {
  const n = xs.length;
  if (n === 0) return '';
  const slopes: number[] = [];
  for (let i = 0; i < n - 1; i++) slopes.push((ys[i + 1]! - ys[i]!) / (xs[i + 1]! - xs[i]! || 1));
  const tangents = xs.map((_, i) => {
    if (i === 0) return slopes[0] ?? 0;
    if (i === n - 1) return slopes[n - 2] ?? 0;
    const a = slopes[i - 1]!;
    const b = slopes[i]!;
    return a * b <= 0 ? 0 : (a + b) / 2;
  });
  let d = `M${xs[0]!.toFixed(2)},${ys[0]!.toFixed(2)}`;
  for (let i = 0; i < n - 1; i++) {
    const dx = (xs[i + 1]! - xs[i]!) / 3;
    d += ` C${(xs[i]! + dx).toFixed(2)},${(ys[i]! + tangents[i]! * dx).toFixed(2)} ${(xs[i + 1]! - dx).toFixed(2)},${(ys[i + 1]! - tangents[i + 1]! * dx).toFixed(2)} ${xs[i + 1]!.toFixed(2)},${ys[i + 1]!.toFixed(2)}`;
  }
  return d;
}

export function HorizonChart({
  points,
  currency,
  height: asked = 150,
  label,
  ticks = 0,
  tip,
}: {
  points: readonly HorizonPoint[];
  currency: string;
  /** Pixels, or `fill`: as tall as the box it is given (a dashboard card of a set size). */
  height?: number | 'fill';
  label: string;
  /** How many dates to print under the line (0: none — the tooltip carries the dates). */
  ticks?: number;
  /** Extra lines for the tooltip of the original point at this index. */
  tip?: (index: number) => React.ReactNode;
}) {
  const t = useT();
  const { privacy } = useTheme();
  const gradient = useId();
  const [width, setWidth] = useState(0);
  const [boxHeight, setBoxHeight] = useState(0);
  const [hover, setHover] = useState<number | null>(null);
  const observer = useRef<ResizeObserver | null>(null);
  // A callback ref: the element may appear after the first render (data arrives later).
  const measure = useCallback((element: HTMLDivElement | null) => {
    observer.current?.disconnect();
    if (!element) return;
    observer.current = new ResizeObserver(([entry]) => {
      setWidth(Math.round(entry!.contentRect.width));
      setBoxHeight(Math.round(entry!.contentRect.height));
    });
    observer.current.observe(element);
  }, []);

  // Dates under the line take 26 px; filling its box, the line takes what is left.
  const tickRoom = ticks > 1 && points.length > 1 ? 26 : 0;
  const height = asked === 'fill' ? Math.max(40, boxHeight - tickRoom) : asked;
  const samples = useMemo(() => resample(points), [points]);
  const shape = useMemo(() => {
    if (samples.length === 0 || width <= AXIS) return null;
    const values = samples.map((s) => s.value);
    let min = Math.min(...values);
    let max = Math.max(...values);
    if (max === min) {
      max += 1;
      min -= 1;
    }
    // Leave air under the lowest point: a flat-ish line should not hug the bottom edge.
    const floor = min - (max - min) * 0.35;
    const plot = height - PAD_TOP - PAD_BOTTOM;
    const y = (v: number) => PAD_TOP + (1 - (v - floor) / (max - floor)) * plot;
    const xs = samples.map((_, i) => AXIS + (i / (samples.length - 1)) * (width - AXIS));
    const ys = values.map(y);
    const line = smoothPath(xs, ys);
    // Grid: round amounts (whole pounds) inside the drawn height, none squeezed against an edge —
    // as many lines as fit at least 30 px apart, so a tall chart gets more of them.
    const top = max + ((max - floor) * PAD_TOP) / plot;
    const bottom = floor - ((max - floor) * PAD_BOTTOM) / plot;
    const span = (top - bottom) / 100;
    const pxPerPound = height / span;
    const step = ([6, 5, 4, 3].map((count) => niceStep(span, count)).find((s) => s * pxPerPound >= 30) ?? niceStep(span, 2)) * 100;
    const grid: { value: number; y: number }[] = [];
    for (let v = Math.ceil(bottom / step) * step; v <= top; v += step) {
      if (y(v) > 8 && y(v) < height - 8) grid.push({ value: v / 100, y: y(v) });
    }
    return { xs, ys, line, grid, area: `${line} L${width},${height} L${AXIS},${height} Z` };
  }, [samples, width, height]);
  const compact = (v: number) => (privacy ? '•••' : new Intl.NumberFormat(t.locale, { notation: 'compact', maximumFractionDigits: 1 }).format(v));

  // The tooltip names a real point from the data (the nearest one), never an interpolated value.
  const original = hover !== null && points.length > 0 ? Math.round((hover / (SAMPLES - 1)) * (points.length - 1)) : null;
  const at = original !== null ? Math.round((original / Math.max(1, points.length - 1)) * (SAMPLES - 1)) : null;
  const point = at !== null && original !== null && shape ? { x: shape.xs[at]!, y: shape.ys[at]!, source: points[original]! } : null;
  const tickDates =
    ticks > 1 && points.length > 1 ? [...new Set(Array.from({ length: ticks }, (_, i) => Math.round((i / (ticks - 1)) * (points.length - 1))))].map((index) => ({ index, date: points[index]!.date })) : [];
  const onMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!shape) return;
    const x = event.clientX - event.currentTarget.getBoundingClientRect().left - AXIS;
    setHover(Math.max(0, Math.min(samples.length - 1, Math.round((x / (width - AXIS)) * (samples.length - 1)))));
  };
  const settle = { duration: 0.7, ease: [0.16, 1, 0.3, 1] as const };

  return (
    <div ref={measure} className="horizon" style={asked === 'fill' ? undefined : { height: height + tickRoom }}>
      {shape && (
        <svg width={width} height={height} role="img" tabIndex={0}
          aria-label={point ? `${label}: ${t.date(point.source.date, 'medium')}${privacy ? '' : `, ${t.money({ minor: point.source.valueMinor, currency })}`}` : label}
          onPointerMove={onMove} onPointerLeave={() => setHover(null)}
          onFocus={() => setHover(SAMPLES - 1)} onBlur={() => setHover(null)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') { setHover(null); return; }
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            setHover((current) => event.key === 'Home' ? 0 : event.key === 'End' ? SAMPLES - 1 : Math.max(0, Math.min(SAMPLES - 1, (current ?? SAMPLES - 1) + (event.key === 'ArrowLeft' ? -1 : 1))));
          }}>
          <defs>
            <linearGradient id={gradient} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--horizon-line)" stopOpacity="0.32" />
              <stop offset="100%" stopColor="var(--horizon-line)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {shape.grid.map((line) => (
            <g key={line.value}>
              <line x1={AXIS} x2={width} y1={line.y} y2={line.y} className="horizon__grid" />
              <text x={AXIS - 8} y={line.y} dy="0.32em" textAnchor="end" className="horizon__amount">
                {compact(line.value)}
              </text>
            </g>
          ))}
          <motion.path initial={false} animate={{ d: shape.area }} transition={settle} fill={`url(#${gradient})`} />
          <motion.path initial={false} animate={{ d: shape.line }} transition={settle} fill="none" stroke="var(--horizon-line)" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
          {/* Today: the line ends in a small lit point. */}
          <motion.circle initial={false} animate={{ cx: shape.xs.at(-1)!, cy: shape.ys.at(-1)! }} transition={settle} r={3.5} fill="var(--horizon-line)" className="horizon__now" />
          {point && (
            <g pointerEvents="none">
              <line x1={point.x} x2={point.x} y1={0} y2={height} className="horizon__rule" />
              <circle cx={point.x} cy={point.y} r={4.5} className="horizon__dot" />
            </g>
          )}
        </svg>
      )}
      {point && original !== null && (
        <div className="chart-tip" style={{ left: Math.min(Math.max(point.x, 80), width - 80), top: Math.max(0, point.y - 62) }}>
          <span className="chart-tip__label">{t.date(point.source.date, 'medium')}</span>
          <strong className="chart-tip__value">{privacy ? '•••' : t.money({ minor: point.source.valueMinor, currency })}</strong>
          {tip?.(original)}
        </div>
      )}
      {tickDates.length > 0 && (
        <div className="horizon__ticks" aria-hidden="true">
          {tickDates.map(({ index, date }, i) => (
            <span key={index} style={{ left: AXIS + (index / (points.length - 1)) * (width - AXIS), transform: i === 0 ? 'none' : i === tickDates.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)' }}>
              {t.date(date, 'dayMonth')}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
